import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCurrentLocationUuid } from 'selectors/session';

import { fetchTrades } from 'features/MyWeek/slice';

import { actions as globalToastActions } from 'components/GlobalToast/slice';

import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';

import {
  clearSelectedRequest,
  fetchUnreadCounts,
  invalidateTab,
} from '../slice';
import { EVENT_ACTIONS, EVENT_CATEGORIES, PRODUCT_AREAS } from '../tracking';
import { handleEmptyJsonResponse } from '../utils';

interface UseRequestActionProps {
  apiFunction: (id: string, args?: any) => Promise<any>;
  itemId: string;
  apiArgs?: any;
  successMessage: string;
  initiatedFrom: string;
  errorMessage?: string;
  buttonText: string;
}

const getEventCategory = (initiatedFrom: string): string => {
  const categoryMap: Record<string, string> = {
    time_off: EVENT_CATEGORIES.TIME_OFF_CARD,
    availability_request: EVENT_CATEGORIES.AVAILABILITY_CARD,
    open_shift_card: EVENT_CATEGORIES.OPEN_SHIFT_CARD,
    cover_shift_card: EVENT_CATEGORIES.COVER_CARD,
    trade_shift_card: EVENT_CATEGORIES.TRADE_CARD,
  };

  return categoryMap[initiatedFrom] || EVENT_CATEGORIES.REQUESTS_FEED;
};

export const useRequestAction = ({
  apiFunction,
  itemId,
  apiArgs,
  successMessage,
  initiatedFrom,
  errorMessage,
  buttonText,
}: UseRequestActionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const currentLocation = useSelector(getCurrentLocationUuid);

  const handleAction = useCallback(async () => {
    // Track button click
    trackUxEvent({
      eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
      eventCategory: getEventCategory(initiatedFrom),
      productArea: PRODUCT_AREAS.REQUESTS,
      properties: {
        button_text: buttonText,
      },
    });

    setIsLoading(true);
    try {
      await handleEmptyJsonResponse(apiFunction(itemId, apiArgs));
      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n(successMessage),
          toastProps: {
            variant: 'success',
            autoHide: true,
            hideCloseIcon: true,
          },
          initiatedFrom,
        })
      );

      // Wait 1 second for backend to finish processing before refetching
      await new Promise(resolve => setTimeout(resolve, 1000));

      dispatch(invalidateTab('my'));
      dispatch(invalidateTab('team'));
      dispatch(fetchUnreadCounts(currentLocation));
      dispatch(clearSelectedRequest());

      // Refresh trades list for trade and cover requests
      if (
        initiatedFrom === 'trade_shift_card' ||
        initiatedFrom === 'cover_shift_card'
      ) {
        dispatch(fetchTrades());
      }
    } catch (error) {
      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n(errorMessage || 'fe_design_base.errors.general'),
          toastProps: { variant: 'error', autoHide: true, hideCloseIcon: true },
          initiatedFrom,
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    itemId,
    apiArgs,
    dispatch,
    successMessage,
    errorMessage,
    initiatedFrom,
    apiFunction,
    currentLocation,
    buttonText,
  ]);

  return { isLoading, handleAction };
};
